import type {
  ChangeEventHandler,
  CSSProperties,
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
} from "react";
import React, { useId } from "react";

import "./kit.css";

export type T_TextFieldInputSlotProps = {
  ariaDescribedBy?: string;
  ariaInvalid?: boolean;
  ariaLabel?: string;
  autoComplete?: string;
  autoFocus: boolean;
  className: string;
  disabled: boolean;
  id: string;
  name?: string;
  placeholder?: string;
  type: "email" | "password" | "search" | "text";
  value?: string;
};

export type T_TextFieldProps = {
  ariaLabel?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  className?: string;
  containerStyle?: CSSProperties;
  /**
   * Callback ref to the inner input/textarea — web mirror of mobile's
   * inputRef (callback form keeps the prop covariant across both elements).
   */
  inputRef?: (element: HTMLInputElement | HTMLTextAreaElement | null) => void;
  disabled?: boolean;
  /** Parent composition owns the visible fill, border, and focus boundary. */
  embedded?: boolean;
  error?: string;
  label?: string;
  leading?: ReactNode;
  multiline?: boolean;
  name?: string;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  placeholder?: string;
  renderInput?: (props: T_TextFieldInputSlotProps) => ReactNode;
  rows?: number;
  trailing?: ReactNode;
  type?: "email" | "password" | "search" | "text";
  value?: string;
};

/**
 * Canonical fill-only text entry surface — web mirror of the mobile TextField.
 * Focus shifts the neutral fill without adding a border or ring. Embedded
 * fields yield their visual/focus boundary to the parent composition. Adds
 * label and error affordances used by web forms.
 */
export function TextField({
  ariaLabel,
  autoComplete,
  autoFocus = false,
  className,
  containerStyle,
  disabled = false,
  embedded = false,
  error,
  inputRef,
  label,
  leading,
  multiline = false,
  name,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  placeholder,
  renderInput,
  rows,
  trailing,
  type = "text",
  value,
}: T_TextFieldProps) {
  const inputId = useId();
  const errorId = useId();
  const hasError = error !== undefined && error !== "";

  const sharedInputProps = {
    "aria-describedby": hasError ? errorId : undefined,
    "aria-label": ariaLabel,
    "aria-invalid": hasError || undefined,
    autoComplete,
    autoFocus,
    className: "lk-text-field__input",
    disabled,
    id: inputId,
    name,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    placeholder,
    value,
  };

  return (
    <div
      className={[
        "lk-text-field",
        className ?? "",
        hasError ? "lk-text-field--error" : "",
        disabled ? "lk-text-field--disabled" : "",
        embedded ? "lk-text-field--embedded" : "",
        multiline ? "lk-text-field--multiline" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={containerStyle}
    >
      {label !== undefined ? (
        <label className="lk-text-field__label" htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      <div className="lk-text-field__surface">
        {leading}
        {renderInput ? (
          renderInput({
            ariaDescribedBy: sharedInputProps["aria-describedby"],
            ariaInvalid: sharedInputProps["aria-invalid"],
            ariaLabel,
            autoComplete,
            autoFocus,
            className: sharedInputProps.className,
            disabled,
            id: inputId,
            name,
            placeholder,
            type,
            value,
          })
        ) : multiline ? (
          <textarea {...sharedInputProps} ref={inputRef} rows={rows} />
        ) : (
          <input {...sharedInputProps} ref={inputRef} type={type} />
        )}
        {trailing}
      </div>
      {hasError ? (
        <span className="lk-text-field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
